import { toISODate } from '@/client/lib/dates';

const MONTHS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const;

const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

/** Parses `YYYY-MM-DD` as a local date (avoids the UTC shift of `new Date(iso)`). */
export function parseISODate(iso: string | undefined | null): Date | undefined {
  if (!iso) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return undefined;
  return makeDate(Number(match[1]), Number(match[2]), Number(match[3])) ?? undefined;
}

/** Builds a local date, rejecting overflow such as 31/02. */
function makeDate(year: number, month: number, day: number): Date | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return d;
}

function startOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function addMonths(date: Date, months: number): Date {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), lastDay));
}

function expandYear(raw: string | undefined, fallback: number): number {
  if (!raw) return fallback;
  const n = Number(raw);
  return raw.length <= 2 ? 2000 + n : n;
}

function monthIndex(word: string): number {
  const i = MONTHS.indexOf(word.slice(0, 3) as (typeof MONTHS)[number]);
  return i === -1 ? -1 : i + 1;
}

/**
 * Parses free-form date text typed by a person. Day comes before month.
 *
 * Accepts: `15/10/2026`, `15.10.26`, `15-10`, `15/10` (current year), `2026-10-15`,
 * `15 oct`, `oct 15 2026`, `today`, `tomorrow`, `yesterday`, `+30`, `-2w`, `+1m`,
 * and weekday names such as `fri` or `next mon`.
 */
export function parseDateInput(input: string): Date | null {
  const text = input.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!text) return null;
  const today = startOfToday();

  if (['today', 'tod', 'now', 't'].includes(text)) return today;
  if (['tomorrow', 'tom', 'tmr', 'tmrw'].includes(text)) return addDays(today, 1);
  if (['yesterday', 'yest', 'yday'].includes(text)) return addDays(today, -1);

  // Relative offsets: +30, -3d, +2w, +1m, +1y
  const rel = /^([+-])\s*(\d{1,4})\s*(d|days?|w|wks?|weeks?|m|mos?|months?|y|yrs?|years?)?$/.exec(
    text,
  );
  if (rel) {
    const n = Number(rel[2]) * (rel[1] === '-' ? -1 : 1);
    const unit = rel[3]?.[0] ?? 'd';
    if (unit === 'w') return addDays(today, n * 7);
    if (unit === 'm') return addMonths(today, n);
    if (unit === 'y') return addMonths(today, n * 12);
    return addDays(today, n);
  }

  // Weekday names: "fri", "friday", "next fri" → the upcoming one (never today).
  const wd = /^(next )?([a-z]{3,9})$/.exec(text);
  if (wd) {
    const idx = WEEKDAYS.indexOf(wd[2].slice(0, 3) as (typeof WEEKDAYS)[number]);
    if (idx !== -1) {
      const diff = (idx - today.getDay() + 7) % 7 || 7;
      return addDays(today, diff);
    }
  }

  // ISO: 2026-10-15
  const iso = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(text);
  if (iso) return makeDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  // Numeric, day first: 15/10/2026, 15.10.26, 15-10, 15 10
  const dm = /^(\d{1,2})[-/. ](\d{1,2})(?:[-/. ](\d{2}|\d{4}))?$/.exec(text);
  if (dm) {
    return makeDate(expandYear(dm[3], today.getFullYear()), Number(dm[2]), Number(dm[1]));
  }

  // Compact digits: 151026 or 15102026
  const compact = /^(\d{2})(\d{2})(\d{2}|\d{4})$/.exec(text);
  if (compact) {
    return makeDate(
      expandYear(compact[3], today.getFullYear()),
      Number(compact[2]),
      Number(compact[1]),
    );
  }

  // Month names: "15 oct", "15 october 2026", "oct 15", "october 15, 2026"
  const dmy = /^(\d{1,2})(?:st|nd|rd|th)? ([a-z]+)\.?,?(?: (\d{2}|\d{4}))?$/.exec(text);
  if (dmy) {
    const m = monthIndex(dmy[2]);
    if (m !== -1) return makeDate(expandYear(dmy[3], today.getFullYear()), m, Number(dmy[1]));
  }
  const mdy = /^([a-z]+)\.? (\d{1,2})(?:st|nd|rd|th)?,?(?: (\d{2}|\d{4}))?$/.exec(text);
  if (mdy) {
    const m = monthIndex(mdy[1]);
    if (m !== -1) return makeDate(expandYear(mdy[3], today.getFullYear()), m, Number(mdy[2]));
  }

  return null;
}

/** `DD/MM/YYYY`, the app's standard short date. */
export function formatShortDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/** e.g. `Wednesday, 15 October 2026`. */
export function formatLongDate(date: Date): string {
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Human distance from today: `Today`, `Tomorrow`, `In 5 days`, `3 weeks ago`… */
export function formatRelativeDay(date: Date): string {
  const days = Math.round((date.getTime() - startOfToday().getTime()) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  const abs = Math.abs(days);
  const [n, unit] =
    abs <= 45
      ? [abs, 'day']
      : abs < 730
        ? [Math.round(abs / 30.44), 'month']
        : [Math.round(abs / 365.25), 'year'];
  const label = `${n} ${unit}${n === 1 ? '' : 's'}`;
  return days > 0 ? `In ${label}` : `${label} ago`;
}

export { addDays as addDaysToDate, startOfToday, toISODate };
